import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { SITE } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { InquiryForm } from "@/components/InquiryForm";

export default function Contact() {
  return (
    <>
      <section className="pt-40 pb-20" data-testid="contact-hero">
        <div className="container-edge max-w-4xl">
          <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Contact Us</p>
          <h1 className="display-1 mt-6">
            Visit us in <span className="italic text-gold">Coimbatore</span>.
          </h1>
          <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed">
            Experience Fibaro live and explore Coimbatore's largest collection of premium interior hardware.
            We'd love to meet you.
          </p>
        </div>
      </section>

      <section className="pb-20" data-testid="contact-info-grid">
        <div className="container-edge grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="aspect-[16/10] overflow-hidden border border-border">
                <iframe
                  title="Map"
                  src={SITE.map}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-full w-full grayscale-[40%] hover:grayscale-0 transition-all duration-700"
                  data-testid="contact-map"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Visit</p>
              <address className="not-italic mt-5 text-lg leading-relaxed">
                {SITE.name}<br />
                {SITE.address.line1}<br />
                {SITE.address.line2}<br />
                {SITE.address.city}<br />
                {SITE.address.state}, {SITE.address.country}
              </address>
            </Reveal>
            <Reveal delay={100}>
              <div className="space-y-6 pt-8 border-t border-border">
                <div data-testid="contact-fibaro-block">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-gold mb-3">Fibaro & Home Automation</p>
                  <div className="space-y-3">
                    <a href={`tel:${SITE.contacts.fibaro.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 hover:text-gold transition-colors" data-testid="contact-phone-fibaro">
                      <Phone className="h-4 w-4 text-gold" />
                      <span className="text-sm">{SITE.contacts.fibaro.phone}</span>
                    </a>
                    <a href={`https://wa.me/${SITE.contacts.fibaro.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 hover:text-gold transition-colors" data-testid="contact-whatsapp-fibaro">
                      <MessageCircle className="h-4 w-4 text-gold" />
                      <span className="text-sm">WhatsApp +{SITE.contacts.fibaro.whatsapp}</span>
                    </a>
                  </div>
                </div>
                <div data-testid="contact-interior-block">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-gold mb-3">Premium Interior Products</p>
                  <div className="space-y-3">
                    <a href={`tel:${SITE.contacts.interior.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 hover:text-gold transition-colors" data-testid="contact-phone-interior">
                      <Phone className="h-4 w-4 text-gold" />
                      <span className="text-sm">{SITE.contacts.interior.phone}</span>
                    </a>
                    <a href={`https://wa.me/${SITE.contacts.interior.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 hover:text-gold transition-colors" data-testid="contact-whatsapp-interior">
                      <MessageCircle className="h-4 w-4 text-gold" />
                      <span className="text-sm">WhatsApp +{SITE.contacts.interior.whatsapp}</span>
                    </a>
                  </div>
                </div>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 hover:text-gold transition-colors pt-6 border-t border-border" data-testid="contact-email">
                  <Mail className="h-4 w-4 text-gold" />
                  <span className="text-sm">{SITE.email}</span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-secondary/40" data-testid="contact-form-section">
        <div className="container-edge grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Send Enquiry</p>
            <h2 className="display-2 mt-5">
              Let's <span className="italic text-gold">talk</span>.
            </h2>
            <p className="mt-8 text-muted-foreground leading-relaxed">
              Drop us a note and our team will reach out within one business day.
            </p>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-background p-8 md:p-12 border border-border">
                <InquiryForm
                  type="contact"
                  testid="contact-form"
                  sourcePage="contact"
                  fields={["name", "phone", "email", "message"]}
                  submitLabel="Send Enquiry"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
